import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { products } from '../data/products';

const featuredProducts = products.slice(0, 6);

export default function ProductShowcase() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-32 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <span
              className={`inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              Featured Products
            </span>
            <h2
              className={`text-3xl md:text-4xl lg:text-5xl font-serif-zh font-bold text-deep-lake mb-4 transition-all duration-700 delay-100 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              核心产品推荐
            </h2>
            <p
              className={`text-lg text-gray-600 max-w-xl transition-all duration-700 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              精选匠心之作，满足不同垂钓场景需求
            </p>
          </div>
          <Link
            to="/products"
            className={`inline-flex items-center gap-2 text-deep-lake font-medium hover:text-dawn-orange transition-all duration-500 group mt-6 md:mt-0 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            查看全部产品
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredProducts.map((product, index) => (
            <Link
              key={product.id}
              to={`/products?id=${product.id}`}
              className={`group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${index === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
              style={{ transitionDelay: `${400 + index * 100}ms` }}
            >
              <div className="aspect-square overflow-hidden bg-gray-50">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-deep-lake" />
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-deep-lake/10 text-deep-lake text-xs font-medium rounded">
                    {product.series}
                  </span>
                </div>
                <h3 className="text-lg font-serif-zh font-bold text-gray-900 mb-2 group-hover:text-deep-lake transition-colors duration-300">
                  {product.name}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {product.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-dawn-orange">
                    {product.price}
                  </span>
                  <span className="text-sm text-gray-400">{product.specs.material}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
